import React, { useMemo, useState } from 'react';
import dialogueThemeService from '../../services/dialogueThemeService';
import styles from './DialogueThemePanel.module.css';

const DialogueThemePanel = ({
  dialogueThemes,
  onDialogueThemesChange,
  activeDialogueThemeId,
  onActiveDialogueThemeIdChange,
  idPrefix = 'dialogue-theme',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#339999');
  const [newFontFamily, setNewFontFamily] = useState('');
  const [newBold, setNewBold] = useState(false);
  const [newItalic, setNewItalic] = useState(false);

  const selectedTheme = useMemo(() => {
    if (!activeDialogueThemeId) return null;
    return dialogueThemes.find((theme) => String(theme.id) === String(activeDialogueThemeId)) || null;
  }, [dialogueThemes, activeDialogueThemeId]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setMessage('Le nom du thème est requis');
      return;
    }

    setCreating(true);
    setMessage('');

    try {
      const payload = {
        name,
        color: newColor,
        fontFamily: newFontFamily.trim() || null,
        isBold: newBold,
        isItalic: newItalic,
        isDefault: dialogueThemes.length === 0,
      };
      const response = await dialogueThemeService.createTheme(payload);
      const createdTheme = response?.item;

      if (createdTheme) {
        onDialogueThemesChange((prev) => {
          const next = payload.isDefault
            ? prev.map((theme) => ({ ...theme, isDefault: false }))
            : prev;
          return [...next, createdTheme];
        });
        onActiveDialogueThemeIdChange(String(createdTheme.id));
        setNewName('');
        setNewFontFamily('');
        setNewBold(false);
        setNewItalic(false);
        setMessage('Thème dialogue enregistré');
      }
    } catch (err) {
      setMessage(err.response?.data?.error || 'Impossible d enregistrer le thème');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (themeId) => {
    try {
      await dialogueThemeService.deleteTheme(themeId);
      onDialogueThemesChange((prev) => prev.filter((theme) => theme.id !== themeId));
      onActiveDialogueThemeIdChange((prev) => (String(prev) === String(themeId) ? '' : prev));
      setMessage('Thème supprimé');
    } catch (err) {
      setMessage(err.response?.data?.error || 'Impossible de supprimer le thème');
    }
  };

  const handleSetDefault = async (theme) => {
    try {
      const response = await dialogueThemeService.updateTheme(theme.id, {
        name: theme.name,
        color: theme.color,
        fontFamily: theme.fontFamily || null,
        isBold: !!theme.isBold,
        isItalic: !!theme.isItalic,
        isDefault: true,
      });
      const updatedTheme = response?.item;
      if (updatedTheme) {
        onDialogueThemesChange((prev) => prev.map((item) => ({
          ...item,
          isDefault: item.id === updatedTheme.id,
        })));
        onActiveDialogueThemeIdChange(String(updatedTheme.id));
        setMessage('Thème par défaut mis à jour');
      }
    } catch (err) {
      setMessage(err.response?.data?.error || 'Impossible de changer le thème par défaut');
    }
  };

  const contentId = `${idPrefix}-dialogue-themes-content`;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-controls={contentId}
        >
          <span>
            <span className={styles.title}>Thèmes de dialogue</span>
            <span className={styles.activeLine}>
              Thème sélectionné:{' '}
              {selectedTheme ? (
                <strong
                  className={styles.activeName}
                  style={{
                    color: selectedTheme.color,
                    fontFamily: selectedTheme.fontFamily || undefined,
                    fontWeight: selectedTheme.isBold ? 700 : 400,
                    fontStyle: selectedTheme.isItalic ? 'italic' : 'normal',
                  }}
                >
                  {selectedTheme.name}
                </strong>
              ) : (
                <strong className={styles.activeName}>Aucun</strong>
              )}
            </span>
          </span>
          <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>
            ▾
          </span>
        </button>
      </div>
      {isOpen && (
        <div id={contentId} className={styles.content}>
          {dialogueThemes.length > 0 ? (
            <div className={styles.selectorBlock}>
              <label className={styles.fieldLabel} htmlFor={`${idPrefix}-existing`}>
                Thème existant
              </label>
              <div className={styles.selectorRow}>
                <select
                  id={`${idPrefix}-existing`}
                  className={styles.select}
                  value={activeDialogueThemeId || ''}
                  onChange={(event) => onActiveDialogueThemeIdChange(event.target.value)}
                >
                  {dialogueThemes.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {theme.name}{theme.isDefault ? ' (defaut)' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.action}
                  onClick={() => selectedTheme && handleSetDefault(selectedTheme)}
                  disabled={!selectedTheme || selectedTheme.isDefault}
                >
                  Defaut
                </button>
                <button
                  type="button"
                  className={styles.action}
                  onClick={() => selectedTheme && handleDelete(selectedTheme.id)}
                  disabled={!selectedTheme}
                >
                  Supprimer
                </button>
              </div>
              {selectedTheme && (
                <p
                  className={styles.previewLine}
                  style={{
                    color: selectedTheme.color,
                    fontFamily: selectedTheme.fontFamily || undefined,
                    fontWeight: selectedTheme.isBold ? 700 : 400,
                    fontStyle: selectedTheme.isItalic ? 'italic' : 'normal',
                  }}
                >
                  Aperçu: "{selectedTheme.name}" actif
                </p>
              )}
            </div>
          ) : (
            <p className={styles.empty}>
              Aucun thème enregistré. Crée ton premier thème de dialogue.
            </p>
          )}
          <div className={styles.form}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor={`${idPrefix}-name`}>
                Nom du thème
              </label>
              <input
                id={`${idPrefix}-name`}
                type="text"
                className={styles.input}
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                placeholder="Ex: Damian, Mera, Alfred..."
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor={`${idPrefix}-color`}>
                Couleur
              </label>
              <input
                id={`${idPrefix}-color`}
                type="color"
                className={styles.color}
                value={newColor}
                onChange={(event) => setNewColor(event.target.value)}
                aria-label="Couleur du thème"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor={`${idPrefix}-font`}>
                Police (optionnel)
              </label>
              <input
                id={`${idPrefix}-font`}
                type="text"
                className={styles.input}
                value={newFontFamily}
                onChange={(event) => setNewFontFamily(event.target.value)}
                placeholder="Ex: Georgia, Impact, Times New Roman"
              />
            </div>
            <div className={styles.checks}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={newBold}
                  onChange={(event) => setNewBold(event.target.checked)}
                />
                <span>Gras</span>
              </label>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={newItalic}
                  onChange={(event) => setNewItalic(event.target.checked)}
                />
                <span>Italique</span>
              </label>
            </div>
            <div className={styles.submitRow}>
              <button
                type="button"
                className={styles.createButton}
                onClick={handleCreate}
                disabled={creating}
              >
                {creating ? 'Ajout...' : 'Ajouter le thème'}
              </button>
            </div>
          </div>
          {message && <p className={styles.message}>{message}</p>}
        </div>
      )}
    </div>
  );
};

export default DialogueThemePanel;
